import { TRequiredFieldConfig } from '~/components/requiredFieldsWarning'
import { TRequiredFieldCandidate } from '~/features/campaigns/components/campaignBuilder/campaignBuilderForm/utils'
import {
  ECampaignBuilderFormKeys,
  ECampaignBuilderStepIds,
  ECampaignType,
} from '~/features/campaigns/models'

const STEP_CAMPAIGN_BASICS = {
  label: 'Campaign Basics',
  id: ECampaignBuilderStepIds.basics,
}

const STEP_MULTI_ASSISTANT_SETUP = {
  label: 'Multi-Assistant Setup',
  id: ECampaignBuilderStepIds.multiAssistantSetup,
}

const STEP_DELIVERY_AND_TIMING = {
  label: 'Delivery & Timing',
  id: ECampaignBuilderStepIds.deliveryAndTiming,
}

const STEP_BEHAVIOR_AND_RULES = {
  label: 'Behavior & Rules',
  id: ECampaignBuilderStepIds.behaviorAndRules,
}

export function getCampaignBuilderStepsMetadata(
  includeMultiAssistantSetup: boolean,
) {
  return [
    STEP_CAMPAIGN_BASICS,
    STEP_DELIVERY_AND_TIMING,
    STEP_BEHAVIOR_AND_RULES,
    ...(includeMultiAssistantSetup ? [STEP_MULTI_ASSISTANT_SETUP] : []),
  ]
}

const CAMPAIGN_BUILDER_STEPS_METADATA = getCampaignBuilderStepsMetadata(false)

const REQUIRED_FIELDS_ONGOING: Partial<
  Record<ECampaignBuilderStepIds, TRequiredFieldConfig[]>
> = {
  [ECampaignBuilderStepIds.basics]: [
    {
      pathField: ECampaignBuilderFormKeys.title,
      friendlyField: 'Campaign title',
    },
    {
      pathField: ECampaignBuilderFormKeys.ofuGoal,
      friendlyField: 'Campaign goal',
    },
    {
      pathField: ECampaignBuilderFormKeys.startDate,
      friendlyField: 'Start Date',
    },
    {
      pathField: ECampaignBuilderFormKeys.ofu_channels,
      friendlyField: 'Communication Channels',
    },
    {
      pathField: ECampaignBuilderFormKeys.emailProviderId,
      friendlyField: 'Email provider',
    },
  ],
  [ECampaignBuilderStepIds.deliveryAndTiming]: [
    {
      pathField: ECampaignBuilderFormKeys.maxReachouts,
      friendlyField: 'Max Reachouts',
    },
    {
      pathField: ECampaignBuilderFormKeys.cadenceCustom,
      friendlyField: 'Custom Cadence Schedule',
    },
    {
      pathField: ECampaignBuilderFormKeys.campaignScheduleSteps,
      friendlyField: 'Schedule steps',
    },
    {
      pathField: ECampaignBuilderFormKeys.phoneIntroMessage,
      friendlyField: 'Phone Intro Message',
    },
    {
      pathField: ECampaignBuilderFormKeys.phoneVoicemailMessage,
      friendlyField: 'Phone Voicemail Message',
    },
    {
      pathField: ECampaignBuilderFormKeys.emailSubject,
      friendlyField: 'Email Subject Message',
    },
    {
      pathField: ECampaignBuilderFormKeys.emailContent,
      friendlyField: 'Email Content Message',
    },
    {
      pathField: ECampaignBuilderFormKeys.textMessage,
      friendlyField: 'Text Message',
    },
  ],
  [ECampaignBuilderStepIds.behaviorAndRules]: [],
  [ECampaignBuilderStepIds.multiAssistantSetup]: [],
}

const REQUIRED_FIELDS_SIMPLE: Partial<
  Record<ECampaignBuilderStepIds, TRequiredFieldConfig[]>
> = {
  [ECampaignBuilderStepIds.basics]: [
    {
      pathField: ECampaignBuilderFormKeys.title,
      friendlyField: 'Campaign title',
    },
    {
      pathField: ECampaignBuilderFormKeys.ofuGoal,
      friendlyField: 'Campaign goal',
    },
    {
      pathField: ECampaignBuilderFormKeys.ofu_channels,
      friendlyField: 'Communication Channels',
    },
    {
      pathField: ECampaignBuilderFormKeys.emailProviderId,
      friendlyField: 'Email provider',
    },
  ],
  [ECampaignBuilderStepIds.deliveryAndTiming]: [
    {
      pathField: ECampaignBuilderFormKeys.messageStrategy,
      friendlyField: 'Message strategy',
    },

    {
      pathField: ECampaignBuilderFormKeys.phoneIntroMessage,
      friendlyField: 'Phone Intro Message',
    },
    {
      pathField: ECampaignBuilderFormKeys.phoneVoicemailMessage,
      friendlyField: 'Phone Voicemail Message',
    },
    {
      pathField: ECampaignBuilderFormKeys.emailSubject,
      friendlyField: 'Email Subject Message',
    },
    {
      pathField: ECampaignBuilderFormKeys.emailContent,
      friendlyField: 'Email Content Message',
    },

    {
      pathField: ECampaignBuilderFormKeys.textMessage,
      friendlyField: 'Text Message',
    },
    {
      pathField: ECampaignBuilderFormKeys.startDate,
      friendlyField: 'Scheduled Date',
    },
  ],
  [ECampaignBuilderStepIds.behaviorAndRules]: [],
  [ECampaignBuilderStepIds.multiAssistantSetup]: [],
}

export const REQUIRED_CAMPAIGN_BUILDER_FIELDS_STEP: Record<
  ECampaignType.ofu | ECampaignType.ofuOneTime,
  Partial<Record<ECampaignBuilderStepIds, TRequiredFieldConfig[]>>
> = {
  [ECampaignType.ofu]: REQUIRED_FIELDS_ONGOING,
  [ECampaignType.ofuOneTime]: REQUIRED_FIELDS_SIMPLE,
}

export function getFormKeyToBuilderStepId(
  campaignType: ECampaignType.ofu | ECampaignType.ofuOneTime,
): Partial<Record<ECampaignBuilderFormKeys, ECampaignBuilderStepIds>> {
  const stepConfig = REQUIRED_CAMPAIGN_BUILDER_FIELDS_STEP[campaignType] ?? {}
  const map: Partial<
    Record<ECampaignBuilderFormKeys, ECampaignBuilderStepIds>
  > = {}
  ;(
    Object.entries(stepConfig) as [
      ECampaignBuilderStepIds,
      TRequiredFieldConfig[],
    ][]
  ).forEach(([stepId, configs]) => {
    configs?.forEach(({ pathField }) => {
      map[pathField as ECampaignBuilderFormKeys] =
        stepId as ECampaignBuilderStepIds
    })
  })

  return map
}

export const CAMPAIGN_BUILDER_STEP_SEARCH_PARAM = 'step'
export const CAMPAIGN_BUILDER_FIELD_SEARCH_PARAM = 'field'
export const CAMPAIGN_BUILDER_ASSISTANT_SEARCH_PARAM = 'assistantId'

export function getRequiredFieldsCandidates(
  campaignType: ECampaignType.ofu | ECampaignType.ofuOneTime,
): TRequiredFieldCandidate[] {
  const stepConfig = REQUIRED_CAMPAIGN_BUILDER_FIELDS_STEP[campaignType] ?? {}
  return (
    Object.entries(stepConfig) as [
      ECampaignBuilderStepIds,
      TRequiredFieldConfig[],
    ][]
  ).flatMap(([stepId, configs]) =>
    (configs ?? []).map(({ pathField, friendlyField }) => ({
      pathField,
      friendlyField,
      stepId,
    })),
  )
}

export default CAMPAIGN_BUILDER_STEPS_METADATA
